import { create } from "zustand";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

export interface AppRole {
  app: string;
  role: string;
  scope_type?: "global" | "territory" | "organization" | "self";
  scope_id?: string;
}

interface AuthState {
  session: Session | null;
  user: User | null;
  appRoles: AppRole[];
  territoryIds: string[];
  isMfaActive: boolean;
  isLoading: boolean;
  error: string | null;
  // actions
  initialize: () => Promise<() => void>;
  signOut: () => Promise<void>;
}

// Parse roles + territories from the Supabase JWT custom claims.
// The JWT claim hook (see supabase/functions/custom-access-token) injects these.
function parseClaims(session: Session | null): {
  appRoles: AppRole[];
  territoryIds: string[];
  isMfaActive: boolean;
} {
  if (!session?.access_token) {
    return { appRoles: [], territoryIds: [], isMfaActive: false };
  }
  try {
    const payload = JSON.parse(atob(session.access_token.split(".")[1]!));
    if (import.meta.env.DEV) {
      // Dev-only: log the MFA-relevant claims so we can verify what Supabase
      // actually emits after an Entra round-trip (BUG-018). Safe — no PII.

      console.debug("[auth] jwt mfa claims", {
        amr: payload.amr,
        aal: payload.aal,
        is_mfa_active: payload.is_mfa_active,
        provider: payload.app_metadata?.provider,
      });
    }
    return {
      appRoles: Array.isArray(payload.app_roles) ? payload.app_roles : [],
      territoryIds: Array.isArray(payload.territory_ids) ? payload.territory_ids : [],
      isMfaActive: Boolean(payload.is_mfa_active),
    };
  } catch {
    return { appRoles: [], territoryIds: [], isMfaActive: false };
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  user: null,
  appRoles: [],
  territoryIds: [],
  isMfaActive: false,
  isLoading: true,
  error: null,

  initialize: async () => {
    // Initial session fetch
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      set({ error: error.message, isLoading: false });
    } else {
      const claims = parseClaims(data.session);
      set({
        session: data.session,
        user: data.session?.user ?? null,
        isLoading: false,
        ...claims,
      });
    }

    // Subscribe to future changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const claims = parseClaims(session);
      set({
        session,
        user: session?.user ?? null,
        ...claims,
      });
    });

    return () => {
      subscription.unsubscribe();
    };
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({
      session: null,
      user: null,
      appRoles: [],
      territoryIds: [],
      isMfaActive: false,
    });
  },
}));
